'use client';

import * as React from 'react';
import { ReactNode, useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { type VariantProps } from 'class-variance-authority';
import './Toast.css';
import {
  toastVariants,
  toastContentVariants,
  toastTextContentVariants,
  toastActionsVariants,
  toastIconVariants,
  toastIconWrapperVariants,
} from './styles';
import { AlertCircleFilledIcon, AlertTriangleFilledIcon, AlertHexagonFilledIcon } from '../icons/Alert';
import { CheckCircleFilledIcon } from '../icons/Check';
import { XIcon } from '../icons/X';
import { Button } from '../Button/Button';

export interface ToastProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof toastVariants>,
    VariantProps<typeof toastContentVariants> {
  /**
   * Toast title (optional)
   */
  title?: string;
  /**
   * Toast description/message
   */
  children: ReactNode;
  /**
   * Optional actions (buttons or custom components)
   */
  actions?: ReactNode;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * ID for testing
   */
  dataTestId?: string;
  /**
   * When true, automatically detects if content exceeds max height (200px)
   * and shows a "View details" button that opens a dialog popup with full content.
   */
  enableDetailDialog?: boolean;
  /**
   * Labels for the detail dialog UI. Use this to provide translated strings.
   */
  detailDialogLabels?: DetailDialogLabels;
  /**
   * Called when "View details" button is clicked. Provided by ToastProvider.
   */
  onViewDetails?: () => void;
}

export interface DetailDialogLabels {
  viewDetails?: string;
  close?: string;
}

const MAX_CONTENT_HEIGHT = 200;

const getIconByType = (type: string) => {
  switch (type) {
    case 'info':
      return <AlertCircleFilledIcon size="sm" className="text-badge-blue" />;
    case 'success':
      return <CheckCircleFilledIcon size="sm" className="text-badge-green" />;
    case 'warning':
      return <AlertTriangleFilledIcon size="sm" className="text-badge-orange" />;
    case 'error':
      return <AlertHexagonFilledIcon size="sm" className="text-badge-red" />;
    case 'neutral':
      return <AlertCircleFilledIcon size="sm" className="text-badge-white" />;
    default:
      return <AlertCircleFilledIcon size="sm" className="text-badge-white" />;
  }
};

/**
 * Toast Component
 *
 * Toast notification component for displaying temporary feedback messages.
 * Appears with shadow and typically shown at the top-right or bottom-center.
 * 
 * Supports 5 types: info, success, warning, error, and neutral.
 * Title is optional.
 */
export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      type = 'info',
      display = 'stacked',
      title,
      children,
      actions,
      onClose,
      className,
      dataTestId = 'toast',
      enableDetailDialog = false,
      detailDialogLabels = {},
      onViewDetails,
      ...props
    },
    ref
  ) => {
    const { viewDetails = 'View details' } = detailDialogLabels;

    const [isOverflowing, setIsOverflowing] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (enableDetailDialog && descriptionRef.current) {
        setIsOverflowing(descriptionRef.current.scrollHeight > MAX_CONTENT_HEIGHT);
      }
    }, [enableDetailDialog, children]);

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          toastVariants({ type }),
          'toast-enter',
          className
        )}
        data-testid={dataTestId}
        {...props}>
        {/* Icon with background wrapper */}
        <div className={cn(toastIconWrapperVariants({ type }), 'self-start')}>
          <div className={cn(toastIconVariants({ type }))}>
            {getIconByType(type || 'info')}
          </div>
        </div>

        {/* Content */}
        <div className={cn(toastContentVariants({ display }), 'self-center')}>
          <div className={cn(toastTextContentVariants({ display }))}>
            {title && (
              <div
                className="font-semibold text-md leading-lg text-primary pt-4xs max-w-[400px] break-words overflow-hidden"
                data-testid={`${dataTestId}-title`}>
                {title}
              </div>
            )}
            <div
              ref={descriptionRef}
              className={cn(
                "text-sm leading-md text-primary max-w-[400px] break-words overflow-hidden",
                isOverflowing ? "line-clamp-[8]" : "whitespace-pre-line"
              )}
              data-testid={`${dataTestId}-description`}>
              {children}
            </div>
          </div>
          {enableDetailDialog ? (
            <div className={cn(toastActionsVariants({ display }))} data-testid={`${dataTestId}-actions`}>
              <Button 
                variant="text" 
                size="S"
                onClick={onViewDetails}
              >
                {viewDetails}
              </Button>
            </div>
          ) : (
            actions && (
              <div className={cn(toastActionsVariants({ display }))} data-testid={`${dataTestId}-actions`}>
                {actions}
              </div>
            )
          )}
        </div>

        {/* Close Button - Always visible */}
        <Button
          variant="text-gray"
          size="S"
          iconOnly
          onClick={onClose}
          className="absolute top-xl right-xl"
          ariaLabel="Close alert"
          dataTestId={`${dataTestId}-close-button`}>
          <XIcon size="sm" />
        </Button>
      </div>

    );
  }
);

Toast.displayName = 'Toast';
